import type {
  LegacyUpgradeState,
  Philosophy,
  RegionId,
  SalesChannelId,
  StationDefinition,
  StationId,
  WineDefinition,
  WineId
} from './types';

export const STATION_ORDER: StationId[] = ['vineyard', 'crush', 'cellar', 'bottling', 'sales'];
export const WINE_ORDER: WineId[] = ['sauvignon', 'rose', 'chardonnay', 'pinot', 'cabernet', 'reserve'];
export const MILESTONES = [10, 25, 50, 100, 200];
export const MANAGER_EFFICIENCIES = [0, 0.35, 0.5, 0.65, 0.82, 1] as const;
export const MAX_MANAGER_LEVEL = MANAGER_EFFICIENCIES.length - 1;
export const MANAGER_COST_GROWTH = 1.9;

export const STATIONS: Record<StationId, StationDefinition> = {
  vineyard: {
    id: 'vineyard', label: 'Vineyard', shortLabel: 'Vines', icon: '🍇', input: null, output: 'grapes',
    action: 'Harvest rows', actionDescription: 'Clip ripe clusters for a burst of grapes.', baseRate: 0.34,
    manualAmount: 3.2, baseCost: 45, managerCost: 1500, capacityCost: 160, baseCapacity: 22
  },
  crush: {
    id: 'crush', label: 'Crush Pad', shortLabel: 'Crush', icon: '⚙', input: 'grapes', output: 'juice',
    action: 'Crank crusher', actionDescription: 'Hand-crank the borrowed crusher.', baseRate: 0.28,
    manualAmount: 2.7, baseCost: 70, managerCost: 5000, capacityCost: 220, baseCapacity: 18
  },
  cellar: {
    id: 'cellar', label: 'Cellar', shortLabel: 'Cellar', icon: '◉', input: 'juice', output: 'wine',
    action: 'Punch down', actionDescription: 'Tend the ferment and add a quality nudge.', baseRate: 0.22,
    manualAmount: 2.1, baseCost: 95, managerCost: 15000, capacityCost: 310, baseCapacity: 14
  },
  bottling: {
    id: 'bottling', label: 'Bottling', shortLabel: 'Bottle', icon: '♜', input: 'wine', output: 'cases',
    action: 'Cork & label', actionDescription: 'Bottle a small run by hand.', baseRate: 0.19,
    manualAmount: 1.7, baseCost: 130, managerCost: 40000, capacityCost: 390, baseCapacity: 12
  },
  sales: {
    id: 'sales', label: 'Sales', shortLabel: 'Tasting', icon: '♧', input: 'cases', output: 'cash',
    action: 'Pour a flight', actionDescription: 'Charm guests into taking bottles home.', baseRate: 0.18,
    manualAmount: 1.35, baseCost: 170, managerCost: 90000, capacityCost: 470, baseCapacity: 10
  }
};

export const WINES: Record<WineId, WineDefinition> = {
  sauvignon: { id: 'sauvignon', name: 'Sauvignon Blanc', shortName: 'Sauv Blanc', color: '#d7d76a', baseValue: 185, processDifficulty: 1, unlockRenown: 0, unlockTier: 1, description: 'Fast, bright, and the reliable engine of early cash flow.', preferredTraits: ['Citrus', 'Gooseberry', 'Fresh Herbs'] },
  rose: { id: 'rose', name: 'Rosé', shortName: 'Rosé', color: '#f29b91', baseValue: 285, processDifficulty: 1.12, unlockRenown: 30, unlockTier: 2, description: 'A patio favorite with seasonal momentum.', preferredTraits: ['Wild Strawberry', 'Rose Petal', 'Sea Breeze'] },
  chardonnay: { id: 'chardonnay', name: 'Chardonnay', shortName: 'Chard', color: '#e5bf56', baseValue: 520, processDifficulty: 1.42, unlockRenown: 110, unlockTier: 3, description: 'The first serious cellar investment.', preferredTraits: ['Pear', 'Lemon Curd', 'Toasted Oak'] },
  pinot: { id: 'pinot', name: 'Pinot Noir', shortName: 'Pinot', color: '#a94e63', baseValue: 940, processDifficulty: 2.15, unlockRenown: 320, unlockTier: 4, description: 'Temperamental, aromatic, and worth the trouble.', preferredTraits: ['Cherry', 'Forest Floor', 'Tea Leaf'] },
  cabernet: { id: 'cabernet', name: 'Cabernet Sauvignon', shortName: 'Cab', color: '#672445', baseValue: 1850, processDifficulty: 3.25, unlockRenown: 780, unlockTier: 5, description: 'Slow, imposing, and built for major releases.', preferredTraits: ['Blackberry', 'Cedar', 'Mild Panic'] },
  reserve: { id: 'reserve', name: 'Reserve Blend', shortName: 'Reserve', color: '#3e173b', baseValue: 5200, processDifficulty: 5.2, unlockRenown: 1700, unlockTier: 7, description: 'The best barrels, held for a defining release.', preferredTraits: ['Cassis', 'Graphite', 'Velvet'] }
};

export const REGIONS: Record<RegionId, {
  name: string;
  tagline: string;
  description: string;
  costMultiplier: number;
  salesMultiplier: number;
  wineBonuses: Partial<Record<WineId, number>>;
  qualityBonus: number;
}> = {
  napa: {
    name: 'Napa Valley', tagline: 'Prestige, polish, and pricey dirt.',
    description: 'Higher operating costs, stronger luxury demand, and a natural advantage for Cabernet.',
    costMultiplier: 1.08, salesMultiplier: 1.1, wineBonuses: { cabernet: 1.18, reserve: 1.08 }, qualityBonus: 1
  },
  sonoma: {
    name: 'Sonoma County', tagline: 'Creative latitude and coastal charm.',
    description: 'Lower costs, broad varietal flexibility, and excellent Pinot Noir and Chardonnay.',
    costMultiplier: 0.94, salesMultiplier: 1.0, wineBonuses: { pinot: 1.13, chardonnay: 1.13, rose: 1.04 }, qualityBonus: 0
  }
};

export const SALES_CHANNELS: Record<SalesChannelId, { name: string; multiplier: number; demand: number; unlockRenown: number; description: string }> = {
  tasting: { name: 'Tasting Room', multiplier: 1.42, demand: 0.75, unlockRenown: 0, description: 'High margin, intimate volume.' },
  club: { name: 'Wine Club', multiplier: 1.28, demand: 0.9, unlockRenown: 80, description: 'Predictable, loyal demand.' },
  restaurant: { name: 'Restaurants', multiplier: 1.2, demand: 1.1, unlockRenown: 180, description: 'Good renown and steady placements.' },
  distributor: { name: 'Distributor', multiplier: 0.78, demand: 2.6, unlockRenown: 500, description: 'Huge volume at a thinner margin.' },
  bulk: { name: 'Bulk Market', multiplier: 0.48, demand: 8, unlockRenown: 0, description: 'Immediate movement, little glory.' }
};

export const PHILOSOPHIES: Record<Philosophy, { label: string; description: string; output: number; quality: number; sales: number }> = {
  boutique: { label: 'Boutique', description: 'Lower volume, meticulous wine.', output: 0.82, quality: 1.11, sales: 1.12 },
  commercial: { label: 'Commercial', description: 'Throughput first, forklifts forever.', output: 1.22, quality: 0.96, sales: 1.03 },
  natural: { label: 'Natural', description: 'More variation and cult potential.', output: 0.94, quality: 1.07, sales: 1.08 },
  luxury: { label: 'Luxury', description: 'Costly presentation, stronger pricing.', output: 0.9, quality: 1.04, sales: 1.22 },
  experimental: { label: 'Experimental', description: 'Odd choices, useful surprises.', output: 1.02, quality: 1.04, sales: 1.04 }
};

export const ESTATE_TIERS = [
  { tier: 1, name: 'Starter Plot', renown: 0, feature: 'Hand press, tiny work shed, bottling bench, and one tasting table.' },
  { tier: 2, name: 'Tasting Garden', renown: 30, feature: 'Rosé, expanded vines, and a permanent hospitality terrace.' },
  { tier: 3, name: 'Crush Pad & Tank Farm', renown: 110, feature: 'Chardonnay, receiving bins, conveyor, and stainless fermentation.' },
  { tier: 4, name: 'Bottling Shed', renown: 320, feature: 'Pinot Noir and a permanent filling, corking, and labeling line.' },
  { tier: 5, name: 'Barrel Cellar', renown: 780, feature: 'Cabernet Sauvignon, proper barrel storage, and patient capital.' },
  { tier: 6, name: 'Wine Caves', renown: 1200, feature: 'Long aging and prestige hospitality.' },
  { tier: 7, name: 'Luxury Estate', renown: 1700, feature: 'Reserve blending and collectors.' },
  { tier: 8, name: 'Regional Wine Empire', renown: 3000, feature: 'Mastery tiers and absurd logistics.' }
] as const;

export const LEGACY_UPGRADES: Record<keyof LegacyUpgradeState, { name: string; description: string; baseCost: number; max: number; icon: string }> = {
  inheritance: { name: 'Grandmother’s Envelope', description: '+$750 starting cash each vintage.', baseCost: 1, max: 20, icon: '✉' },
  oldVines: { name: 'Old Vine Memory', description: '+18% vineyard output per rank.', baseCost: 1, max: 25, icon: '♣' },
  cellarLore: { name: 'Cellar Lore', description: '+1.5 baseline quality per rank.', baseCost: 2, max: 20, icon: '◉' },
  nightShift: { name: 'Night Shift Managers', description: 'Extends offline cap: 8h → 24h → 72h → unlimited.', baseCost: 3, max: 3, icon: '☾' },
  tradeContacts: { name: 'Trade Contacts', description: '+15% sales value per rank.', baseCost: 2, max: 25, icon: '◆' },
  blueprints: { name: 'Annotated Blueprints', description: '-4% station and capacity costs per rank.', baseCost: 2, max: 15, icon: '▤' }
};
